import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../../../contexts/userContext";
import { Training, TrainingStatus } from "../../../models/training";
import {
  getDocs,
  query,
  orderBy,
  addDoc,
  collection,
} from "firebase/firestore";
import {
  CircularProgress,
  Tooltip,
  Divider,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { trackEvent } from "../../../utils/analytics";
import TrainingDb from "../../../db/trainingDb";
import moment from "moment";
import { db } from "../../../firebase";
import {
  IconDownload,
  IconCheck,
  IconX,
  IconClock,
  IconLoader,
  IconHelpCircle,
  IconPhotoSearch,
  IconUpload,
  IconBrain,
  IconSparkles,
} from "@tabler/icons-react";
import CustomButton from "../../utils/customButton";
import JSZip from "jszip";
import { useNavigate } from "react-router-dom";
import ModelDb from "../../../db/modelDb";
import { Model } from "../../../models/model";
import Functions from "../../../callable/functions";

const ModelsScreen: React.FC = () => {
  const user = useContext(UserContext);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newTrainingName, setNewTrainingName] = useState("");
  const [newTrainingDescription, setNewTrainingDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  const navigate = useNavigate();
  const functions = new Functions();

  // Check if there are any completed models (models are only created when training is completed)
  const [models, setModels] = useState<Model[]>([]);

  useEffect(() => {
    fetchTrainings();
  }, [user]);

  useEffect(() => {
    const fetchModels = async () => {
      if (user) {
        try {
          const modelDb = new ModelDb();
          const modelsQuery = query(
            modelDb.collection({ userId: user.id, modelId: "" }),
            orderBy("createdAt", "desc")
          );
          const querySnapshot = await getDocs(modelsQuery);
          const modelsData = querySnapshot.docs.map((doc) =>
            Model.fromFirestore(doc)
          );
          setModels(modelsData);
        } catch (error) {
          console.error("Error fetching models:", error);
        }
      }
    };

    fetchModels();
  }, [user]);

  const fetchTrainings = async () => {
    if (user) {
      setLoading(true);
      try {
        const trainingDb = new TrainingDb();
        const trainingsQuery = query(
          trainingDb.collection({ userId: user.id, trainingId: "" }),
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(trainingsQuery);
        const trainingsData = querySnapshot.docs.map((doc) =>
          Training.fromFirestore(doc)
        );
        setTrainings(trainingsData);
      } catch (error) {
        console.error("Error fetching trainings:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileSelect = (acceptedFiles: File[]) => {
    if (acceptedFiles.length < 10) {
      alert("Please select at least 10 images for better training results.");
      return;
    }
    setSelectedFiles(acceptedFiles);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewTrainingName("");
    setNewTrainingDescription("");
    setSelectedFiles([]);
  };

  const handleStartTraining = async () => {
    if (user && selectedFiles.length > 0) {
      setUploading(true);
      try {
        const newTrainingRef = await addDoc(
          collection(db, `users/${user.id}/trainings`),
          {
            name: newTrainingName,
            description: newTrainingDescription,
            status: TrainingStatus.PENDING,
            createdAt: moment().toDate(),
            updatedAt: moment().toDate(),
          }
        );

        const zip = new JSZip();

        for (const file of selectedFiles) {
          const fileContent = await file.arrayBuffer();
          zip.file(file.name, fileContent);
        }

        const zipContent = await zip.generateAsync({ type: "blob" });

        const storage = getStorage();
        const storageRef = ref(
          storage,
          `users/${user.id}/trainings/${newTrainingRef.id}.zip`
        );
        await uploadBytes(storageRef, zipContent);

        const publicLink = await getDownloadURL(storageRef);

        const result = await functions.updateTrainingWithLink({
          userId: user.id,
          trainingId: newTrainingRef.id,
          publicLink: publicLink,
        });

        fetchTrainings();
        trackEvent("model_training_started");
        handleCloseDialog();
      } catch (error) {
        console.error("Error uploading images:", error);
      } finally {
        setUploading(false);
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileSelect,
    accept: { "image/*": [".png", ".jpg", ".jpeg"] },
    multiple: true,
    minSize: 1,
  });

  const getStatusIcon = (status: TrainingStatus) => {
    switch (status) {
      case TrainingStatus.COMPLETED:
        return <IconCheck size={20} className="text-green-500" />;
      case TrainingStatus.FAILED:
        return <IconX size={20} className="text-red-500" />;
      case TrainingStatus.IN_PROGRESS:
        return <IconLoader size={20} className="text-blue-500 animate-spin" />;
      default:
        return <IconClock size={20} className="text-yellow-500" />;
    }
  };

  const getStatusMessage = (
    status: TrainingStatus,
    modelId: string,
    errorMessage?: string
  ) => {
    if (status === TrainingStatus.PENDING) {
      return (
        <span className="text-sm text-yellow-500 ml-2">
          Your training is queued and will be complete in a few minutes. Don't
          worry, we will notify you via email when your model is ready to use.
        </span>
      );
    } else if (status === TrainingStatus.IN_PROGRESS) {
      return (
        <span className="text-sm text-blue-500 ml-2">
          Your model is currently being trained. This process typically takes a
          few hours. We'll notify you via email once it's ready.
        </span>
      );
    } else if (status === TrainingStatus.COMPLETED) {
      return (
        <span className="text-sm text-green-500 ml-2">
          Your model is ready.
        </span>
      );
    } else if (status === TrainingStatus.FAILED) {
      return (
        <span className="text-sm text-red-500 ml-2">
          Training failed: {errorMessage || "Unknown error"}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="w-full min-h-screen bg-[#0B0B0F] text-white p-4 md:px-32 md:py-8 font-custom">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-base font-custom font-bold">Model Training</h4>
        {models.length > 0 && (
          <CustomButton onClick={() => navigate("/app/thumb-genie/pipeline")}>
            Generate Thumbnails
          </CustomButton>
        )}
      </div>
      <Divider
        sx={{
          borderColor: "#282828",
          borderBottomWidth: 2,
          marginBottom: 4,
        }}
      />

      {/* Merged Progress and Welcome Card */}
      <div className="mb-8 bg-gradient-to-r from-green-500/10 to-green-600/10 border border-green-500/20 rounded-lg p-6">
        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between relative">
            {/* Progress line */}
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-700 z-0">
              <div
                className={`h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500 ${(() => {
                  const currentStep =
                    models.length > 0 ? 3 : trainings.length > 0 ? 2 : 1;
                  return currentStep === 1
                    ? "w-0"
                    : currentStep === 2
                    ? "w-1/2"
                    : "w-full";
                })()}`}
              />
            </div>

            {[
              {
                id: 1,
                title: "Upload & Train Model",
                description:
                  "Upload your thumbnails to train your custom model",
                icon: IconUpload,
              },
              {
                id: 2,
                title: "Model Ready",
                description: "Your model is being trained or is ready to use",
                icon: IconBrain,
              },
              {
                id: 3,
                title: "Generate Thumbnails",
                description:
                  "Create unlimited thumbnails with your trained model",
                icon: IconSparkles,
              },
            ].map((step) => {
              const currentStep =
                models.length > 0 ? 3 : trainings.length > 0 ? 2 : 1;
              const hasActiveTraining = trainings.some(
                (t) =>
                  t.status === TrainingStatus.PENDING ||
                  t.status === TrainingStatus.IN_PROGRESS
              );
              const isCompleted = currentStep > step.id;
              const isCurrent = currentStep === step.id;
              const isActive =
                isCurrent &&
                (step.id === 1 || (step.id === 2 && hasActiveTraining));
              const IconComponent = step.icon;

              return (
                <div
                  key={step.id}
                  className="flex flex-col items-center relative z-10"
                >
                  {/* Step circle */}
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                      isCompleted
                        ? "bg-green-500 text-white"
                        : isCurrent
                        ? "bg-green-500 text-white"
                        : "bg-gray-700 text-gray-400"
                    }`}
                  >
                    {isCompleted ? (
                      <IconCheck size={20} />
                    ) : isActive ? (
                      <IconLoader size={20} className="animate-spin" />
                    ) : (
                      <IconComponent size={20} />
                    )}
                  </div>

                  {/* Step content */}
                  <div className="text-center max-w-32">
                    <h4
                      className={`text-sm font-semibold mb-1 ${
                        isCompleted || isCurrent
                          ? "text-white"
                          : "text-gray-500"
                      }`}
                    >
                      {step.title}
                    </h4>
                    <p
                      className={`text-xs ${
                        isCompleted || isCurrent
                          ? "text-gray-300"
                          : "text-gray-600"
                      }`}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Current step hint */}
          {(() => {
            const currentStep =
              models.length > 0 ? 3 : trainings.length > 0 ? 2 : 1;
            const hasActiveTraining = trainings.some(
              (t) =>
                t.status === TrainingStatus.PENDING ||
                t.status === TrainingStatus.IN_PROGRESS
            );

            if (currentStep === 2 && hasActiveTraining) {
              return (
                <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-blue-400 text-sm text-center">
                    🎯 Your model is being trained! We'll email you when it's
                    ready (usually takes a few minutes)
                  </p>
                </div>
              );
            } else if (currentStep === 2 && !hasActiveTraining) {
              return (
                <div className="mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 text-sm text-center">
                    ✅ Your model is ready! Click "Generate Thumbnails" to start
                    creating
                  </p>
                </div>
              );
            } else if (currentStep === 3) {
              return (
                <div className="mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 text-sm text-center">
                    🚀 All set! You can now generate unlimited thumbnails with
                    your trained model
                  </p>
                </div>
              );
            }
          })()}
        </div>

        {/* Welcome content for new users */}
        {trainings.length === 0 && (
          <div className="border-t border-green-500/20 pt-6">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <IconPhotoSearch size={24} className="text-green-500" />
              </div>
              <div className="flex-grow">
                <h3 className="text-white font-bold mb-2">
                  Welcome to YouGenie, let's train your first model!
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  To generate personalized thumbnails that match your channel's
                  style, you'll need to train a custom model first. This is how
                  YouGenie creates thumbnails that truly represent your brand.
                </p>
                <div className="bg-[#1A1A1F] p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">
                    How it works:
                  </h4>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• Upload 10-20 of your best thumbnails</li>
                    <li>• We'll train a custom AI model with your style</li>
                    <li>
                      • Training takes a few minutes - we'll email you when
                      ready
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed border-[#282828] rounded-lg p-8 mb-8 text-center cursor-pointer h-40 flex flex-col justify-center ${
          isDragActive ? "bg-gray-700" : ""
        }`}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p className="text-base">Drop the image files here ...</p>
        ) : (
          <div>
            <p className="text-base mb-2">
              Drag 'n' drop image files here, or click to select files
            </p>
            <p className="text-sm text-gray-400">
              Include 10-20 thumbnails with letterings removed for better
              training (.png, .jpg, .jpeg).
            </p>
          </div>
        )}
      </div>

      {/* Training Examples Section */}
      <div className="mb-8">
        <div className="p-4 bg-[#1A1A1F] rounded-lg">
          <p className="text-sm text-gray-400 mb-4">
            Here is an example of a good training set for a YouTube channel:
          </p>

          <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
              <div key={num} className="aspect-w-16 aspect-h-9">
                <img
                  src={`/images/training-examples/${num}.jpg`}
                  alt={`Training example ${num}`}
                  className="object-cover rounded-lg"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add help toggle button */}
      <button
        onClick={() => setShowHelp(!showHelp)}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-2"
      >
        <IconHelpCircle size={16} />
        {showHelp ? "Hide help" : "Need help getting your thumbnails?"}
      </button>

      {/* Conditionally render help section */}
      {showHelp && (
        <div className="mb-8 p-4 bg-[#1A1A1F] rounded-lg">
          <p className="text-sm text-gray-400 mb-2">
            You can download YouTube thumbnails using this format:
          </p>
          <code className="block bg-[#0B0B0F] p-3 rounded text-sm text-gray-300 font-mono">
            https://i.ytimg.com/vi/
            <span className="text-red-500">{`{VIDEO_ID}`}</span>
            /maxresdefault.jpg
          </code>
          <p className="text-sm text-gray-400 mt-2">
            Replace <span className="text-red-500">{`{VIDEO_ID}`}</span> with
            the ID from your YouTube video URL (e.g., ktyJIj6i4Qw)
          </p>
        </div>
      )}

      {uploading && (
        <div className="mb-4 h-1 w-full bg-gray-700">
          <div className="h-1 bg-red-500 animate-pulse"></div>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center items-center mt-8">
          <CircularProgress color="inherit" size={40} />
        </div>
      ) : trainings.length === 0 ? (
        <div className="text-center mt-8">
          <p className="text-sm text-gray-400">
            No trainings found. Please upload image files to start a new
            training.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {trainings.map((training, index) => (
            <React.Fragment key={training.id}>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-6 flex-grow">
                  <Tooltip title={training.status} arrow placement="top">
                    {getStatusIcon(training.status)}
                  </Tooltip>
                  <div className="flex-grow">
                    <div className="flex items-center space-x-2">
                      <p className="text-base font-semibold truncate">
                        {training.name}
                      </p>
                      {training.description && (
                        <p className="text-sm text-gray-400 truncate">
                          / {training.description}
                        </p>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {training.createdAt.format("DD MMM YY")}
                      {getStatusMessage(
                        training.status,
                        training.modelId || "",
                        training.errorMessage
                      )}
                    </p>
                  </div>
                </div>
                {training.publicLink && (
                  <Tooltip title="Download ZIP" arrow>
                    <a
                      href={training.publicLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white ml-4"
                    >
                      <IconDownload size={20} />
                    </a>
                  </Tooltip>
                )}
              </div>
              {index < trainings.length - 1 && (
                <div className="border-t border-gray-800" />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{
          style: {
            backgroundColor: "#0B0B0F",
            color: "white",
            fontFamily: "'YouTubeSansRegular', sans-serif",
            minWidth: "600px",
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'YouTubeSansRegular', sans-serif",
            fontSize: "1rem",
            paddingBottom: 2,
          }}
        >
          Start Model Training
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Model"
            type="text"
            fullWidth
            value={newTrainingName}
            onChange={(e) => setNewTrainingName(e.target.value)}
            placeholder="e.g., Mr. Beast v1"
            helperText="Give your model a clear, descriptive name so you can recognize it later."
            InputLabelProps={{
              style: { color: "rgba(255, 255, 255, 0.7)" },
            }}
            InputProps={{
              style: { color: "white" },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#282828",
                },
                "&:hover fieldset": {
                  borderColor: "#484848",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FF0000",
                },
              },
            }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={newTrainingDescription}
            onChange={(e) => setNewTrainingDescription(e.target.value)}
            placeholder="e.g., This model will be trained with the latest thumbnails from my gaming channel featuring dark backgrounds and bright text overlays."
            helperText="Describe what type of thumbnails you're training on (e.g., 'Gaming thumbnails with dark backgrounds' or 'Tech review thumbnails'). This is NOT where you request thumbnails - that comes after training."
            InputLabelProps={{
              style: { color: "rgba(255, 255, 255, 0.7)" },
            }}
            InputProps={{
              style: { color: "white" },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#282828",
                },
                "&:hover fieldset": {
                  borderColor: "#484848",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FF0000",
                },
              },
            }}
          />
          <p className="mt-4 text-sm text-gray-400">
            Selected files: {selectedFiles.map((file) => file.name).join(", ")}
          </p>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={handleCloseDialog}>Cancel</CustomButton>
          <CustomButton
            onClick={handleStartTraining}
            disabled={!newTrainingName || uploading}
          >
            {uploading ? "Starting..." : "Start"}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ModelsScreen;
